import { ActionMenu, type ActionMenuItem } from "../shell/ActionMenu";

/**
 * Sprint 14g — the universal "⋮" row menu mounted on every cockpit list panel
 * (Tasks / Tasks / Routines / Activity / Pages / Prompts / Memories /
 * Files). A thin wrapper over the portal-based {@link ActionMenu} (Sprint 12b)
 * that assembles the right items for a row's state:
 *
 * - active row → Archive  (+ Delete when canDelete)
 * - archived row → Restore (+ Delete when canDelete)
 *
 * Delete is owner-only: the item is entirely absent for members (canDelete
 * false), not just disabled. Archive/Restore are member-grade. The panel wires
 * the callbacks — onArchive fires the archive mutation (with an undo toast),
 * onDelete opens the confirm-delete modal.
 */
export interface PanelRowMenuProps {
  isArchived?: boolean;
  /** True only for workspace owners — gates the Delete item. */
  canDelete?: boolean;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
  /** Extra items rendered above the archive/delete block (e.g. Edit). */
  extraItems?: ActionMenuItem[];
}

export function PanelRowMenu({
  isArchived,
  canDelete,
  onArchive,
  onRestore,
  onDelete,
  extraItems,
}: PanelRowMenuProps) {
  const items: ActionMenuItem[] = [...(extraItems ?? [])];

  if (isArchived) {
    if (onRestore) items.push({ label: "Restore", icon: "restore", onClick: onRestore });
  } else if (onArchive) {
    items.push({ label: "Archive", icon: "archive", onClick: onArchive });
  }

  if (canDelete && onDelete) {
    items.push({ label: "Delete", icon: "trash", variant: "danger", onClick: onDelete });
  }

  if (items.length === 0) return null;
  return <ActionMenu items={items} />;
}
